import React, { useState, useEffect, type PropsWithChildren } from "react"
import GoogleMapReact from "google-map-react"

import {
    useMapOptions,
    getMapFitBounds,
    getMapTopLat,
    getMapBottomLat,
    getMapRightLng,
    getMapLeftLng,
    isMapSizeNew,
} from "~components/shared/map/map-helpers"

interface MapProps extends PropsWithChildren {
    readonly options?: (maps: GoogleMapReact.Maps) => GoogleMapReact.MapOptions
    readonly markers: { lat: number; lng: number }[]
}

export default function Map({ options, markers, children }: MapProps) {
    const [mapCenter, setMapCenter] = useState({ lat: 37.0902, lng: -95.7129 }) // United States' geolocation
    const [mapZoom, setMapZoom] = useState(10)
    const [mapSize, setMapSize] = useState<GoogleMapReact.Size>({
        width: 0,
        height: 0,
    })

    const mapOptions = useMapOptions(options)

    const topLat = getMapTopLat(markers)
    const bottomLat = getMapBottomLat(markers)
    const rightLng = getMapRightLng(markers)
    const leftLng = getMapLeftLng(markers)

    useEffect(() => {
        if (
            topLat &&
            bottomLat &&
            rightLng &&
            leftLng &&
            Object.keys(mapSize).length > 0
        ) {
            const fitBounds = getMapFitBounds(
                topLat,
                bottomLat,
                rightLng,
                leftLng,
                mapSize
            )
            setMapCenter(fitBounds.center)
            setMapZoom(fitBounds.zoom)
        }
    }, [topLat, bottomLat, rightLng, leftLng, mapSize])

    const handleMapChange = (map: GoogleMapReact.ChangeEventValue) => {
        setMapCenter(map.center)
        setMapZoom(map.zoom)
        if (isMapSizeNew(mapSize, map.size)) {
            setMapSize(map.size)
        }
    }

    return (
        <GoogleMapReact
            bootstrapURLKeys={{ key: process.env.GATSBY_GCP_KEY || "" }}
            center={mapCenter}
            zoom={mapZoom}
            options={mapOptions}
            onChange={handleMapChange}
        >
            {children}
        </GoogleMapReact>
    )
}
